import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { createRouter } from '@/app/router';
import { Providers } from '@/app/providers';
import { getWsClient } from '@/lib/ws/ws-client';
import { loadKioskConfig } from '@/lib/config/kiosk-config';
import { KioskConfigError } from '@/screens/kiosk-config-error';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/600.css';
import '@fontsource/quicksand/700.css';
import '@fontsource/caveat/500.css';
import '@fontsource/caveat/600.css';
import '@fontsource/caveat/700.css';
import '@/styles/globals.css';

async function bootstrap() {
  // Mark dev so the cursor stays visible for mouse-based development.
  if (import.meta.env.DEV) document.body.setAttribute('data-dev', 'true');

  if (__USE_MOCKS__ && import.meta.env.VITE_USE_MOCKS !== 'false') {
    const { startMockWorker } = await import('@/mocks/browser');
    await startMockWorker();
  }

  const root = createRoot(document.getElementById('root')!);

  // This device's kiosk identity, before anything can send a request with it.
  // A tablet that can't identify itself must not start: falling back to a
  // default would mis-attribute its orders and print on another kiosk's printer.
  try {
    await loadKioskConfig();
  } catch (err) {
    root.render(<KioskConfigError detail={err instanceof Error ? err.message : String(err)} />);
    return;
  }

  // Establish WS connection (drives connectivity-store.wsConnected).
  getWsClient().connect();

  // Built after loadKioskConfig() resolves: the basename is derived from this
  // device's own directory, same signal the config fetch used.
  const router = createRouter();

  root.render(
    <StrictMode>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  );
}

void bootstrap();
